import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/utilities';

/**
 * @class ProductPrice
 * @extends HTMLElement
 *
 * Price display element that keeps itself in sync with `ThemeEvents.variantUpdate`.
 */
class ProductPrice extends HTMLElement {
  #servingData = {};
  #closestSection = null;

  connectedCallback() {
    this.#servingData = this.#parseServingData();

    this.#closestSection = this.closest('.shopify-section');
    this.#closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    this.#closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#closestSection = null;
  }

  get #productId() {
    return this.getAttribute('data-product-id');
  }

  get #priceElement() {
    return this.querySelector('[data-ref="price"]');
  }

  get #compareAtPriceElement() {
    return this.querySelector('[data-ref="compareAtPrice"]');
  }

  get #servingPriceElement() {
    return this.querySelector('[data-ref="servingPrice"]');
  }

  /**
   * 'variant' (default) formats price/compare-at from the raw variant JSON attached to the event.
   * 'html' reads the server-rendered price out of the fetched section fragment.
   */
  get #source() {
    return this.getAttribute('data-source') === 'html' ? 'html' : 'variant';
  }

  #parseServingData() {
    try {
      const json = this.querySelector('script[data-ref="servingData"]')?.textContent;
      return json ? JSON.parse(json) : {};
    } catch (error) {
      console.error(error);
      return {};
    }
  }

  #onVariantUpdate = (event) => {
    if (event.detail?.data?.productId !== this.#productId) return;

    const variant = event.detail?.variant;
    if (!variant) return;

    if (this.#source === 'html') {
      this.#updatePriceFromHtml(event.detail?.data?.html);
    } else {
      this.#updatePriceFromVariant(variant);
    }
  };

  #updatePriceFromVariant(variant) {
    const hasSale = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (this.#priceElement) {
      this.#priceElement.textContent = formatMoney(variant.price, Theme.settings.currency_format);
    }

    if (this.#compareAtPriceElement) {
      this.#compareAtPriceElement.textContent = hasSale
        ? formatMoney(variant.compare_at_price, Theme.settings.currency_format)
        : '';
      this.#compareAtPriceElement.hidden = !hasSale;
    }

    const servingPriceElement = this.#servingPriceElement;
    if (servingPriceElement) {
      const servingData = this.#servingData[variant.id];

      if (servingData?.serving_price) {
        const labelTemplate = servingPriceElement.dataset.label ?? '%%%PRICE%%%';
        servingPriceElement.textContent = `(${labelTemplate.replace('%%%PRICE%%%', servingData.serving_price)})`;
        servingPriceElement.hidden = false;
      } else {
        servingPriceElement.textContent = '';
        servingPriceElement.hidden = true;
      }
    }
  }

  #updatePriceFromHtml(html) {
    if (!html) return;

    const currentPriceElement = this.querySelector('[data-ref="priceContainer"]');
    const newPriceElement = this.id
      ? html.querySelector(`#${this.id} [data-ref="priceContainer"]`)
      : html.querySelector('product-price [data-ref="priceContainer"]');

    if (!currentPriceElement || !newPriceElement) return;

    if (currentPriceElement.innerHTML !== newPriceElement.innerHTML) {
      currentPriceElement.innerHTML = newPriceElement.innerHTML;
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
